/**
 * Queries and mutations for AREA-EVENTS: event types, rules, simulation,
 * tools, messages, and send requests.
 *
 * Event type keys reuse the shared `queryKeys.eventTypes` root so the command
 * palette and pickers refresh on invalidation. Messages and requests have
 * their own roots here.
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '@/api/client'
import { fetchAll } from '@/api/fetch-all'
import { queryKeys, type ListParams } from '@/api/keys'
import { proposalKeys } from '@/api/queries/proposals'
import { useCursorPages, useInfiniteList } from '@/api/pagination'
import { MAX_PAGE_SIZE } from '@/api/types'
import { apiObject, type JsonObject } from './json'
import { toMessageQuery, type MessageSearch } from './message-filters'
import type {
  CelCheckRequest,
  CelCheckResponse,
  CreateEventType,
  ResendRequest,
  RuleInput,
  SimulateRequest,
  SimulateSequenceRequest,
  TestSendRequest,
  UpdateEventType,
} from './types'

export const eventTypeKeys = {
  ...queryKeys.eventTypes,
  context: (id: string) => ['event-types', 'detail', id, 'context'] as const,
}

export const messageKeys = {
  all: ['messages'] as const,
  list: (params: Record<string, unknown>) => ['messages', 'list', params] as const,
  detail: (id: string) => ['messages', 'detail', id] as const,
}

export const requestKeys = {
  all: ['requests'] as const,
  detail: (id: string) => ['requests', 'detail', id] as const,
}

export const senderKeys = {
  all: ['senders'] as const,
  options: (app: string) => ['senders', 'list', { app, include_shared: true, all: true }] as const,
}

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

export function useEventTypesList(params: ListParams & { enabled?: boolean }) {
  const { enabled, ...rest } = params
  return useInfiniteList({
    queryKey: eventTypeKeys.list({ ...rest, enabled }),
    fetchPage: (cursor) =>
      unwrap(
        api.GET('/admin/v1/event-types', {
          params: {
            query: {
              app: rest.app,
              q: rest.q || undefined,
              enabled,
              limit: 100,
              cursor,
            },
          },
        }),
      ),
  })
}

export const eventTypeQuery = (id: string) =>
  queryOptions({
    queryKey: eventTypeKeys.detail(id),
    queryFn: () => unwrap(api.GET('/admin/v1/event-types/{id}', { params: { path: { id } } })),
  })

/** Completion tree for CEL and template editors. Rarely changes; cache generously. */
export const eventTypeContextQuery = (id: string) =>
  queryOptions({
    queryKey: eventTypeKeys.context(id),
    queryFn: () =>
      unwrap(api.GET('/admin/v1/event-types/{id}/context', { params: { path: { id } } })),
    staleTime: 5 * 60_000,
    retry: false,
  })

/** Every event key in scope, for the message log's event filter. */
export const eventKeysQuery = (app: string | undefined) =>
  queryOptions({
    queryKey: eventTypeKeys.list({ app, all: true }),
    queryFn: () =>
      fetchAll((cursor) =>
        unwrap(
          api.GET('/admin/v1/event-types', {
            params: { query: { app, limit: MAX_PAGE_SIZE, cursor } },
          }),
        ),
      ),
    staleTime: 60_000,
  })

export function useCreateEventType() {
  return useMutation({
    mutationFn: (body: CreateEventType) => unwrap(api.POST('/admin/v1/event-types', { body })),
    meta: {
      invalidates: [eventTypeKeys.all],
      successMessage: 'Event type created',
      errorToast: false,
    },
  })
}

export function useUpdateEventType(id: string) {
  return useMutation({
    mutationFn: (body: UpdateEventType) =>
      unwrap(api.PATCH('/admin/v1/event-types/{id}', { params: { path: { id } }, body })),
    // A save can apply a settings proposal (`proposal_id`), so refresh proposals too.
    meta: { invalidates: [eventTypeKeys.all, proposalKeys.all], errorToast: false },
  })
}

export function useReplaceRules(id: string) {
  return useMutation({
    mutationFn: ({ rules, proposalId }: { rules: RuleInput[]; proposalId?: string }) =>
      unwrap(
        api.PUT('/admin/v1/event-types/{id}/rules', {
          params: { path: { id } },
          body: { rules, proposal_id: proposalId ?? null },
        }),
      ),
    // Templates list rules that use them (used_by); a save can apply a rules
    // proposal (`proposal_id`). Refresh both.
    meta: {
      invalidates: [eventTypeKeys.all, queryKeys.templates.all, proposalKeys.all],
      errorToast: false,
    },
  })
}

export function useDeleteEventType() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/admin/v1/event-types/{id}', { params: { path: { id } } })),
    // Refresh lists and pickers only. The editor is still mounted until the
    // caller navigates away, so dropping or refetching the detail here would
    // 404. Call forget(id) after navigating.
    meta: {
      invalidates: [['event-types', 'list'], queryKeys.templates.all],
      successMessage: 'Event type deleted',
    },
  })
  const forget = (id: string) => qc.removeQueries({ queryKey: eventTypeKeys.detail(id) })
  return { ...mutation, forget }
}

export function useSimulate(id: string) {
  return useMutation({
    mutationFn: (body: SimulateRequest) =>
      unwrap(api.POST('/admin/v1/event-types/{id}/simulate', { params: { path: { id } }, body })),
    meta: { errorToast: false },
  })
}

/** Run a sequence of events through the rules and send policies on a virtual clock. */
export function useSimulateSequence(appId: string) {
  return useMutation({
    mutationFn: (body: SimulateSequenceRequest) =>
      unwrap(
        api.POST('/admin/v1/applications/{id}/simulate-sequence', {
          params: { path: { id: appId } },
          body,
        }),
      ),
    meta: { errorToast: false },
  })
}

export function useInferSchema() {
  return useMutation({
    mutationFn: (samples: JsonObject[]) =>
      unwrap(
        api.POST('/admin/v1/tools/schema/infer', {
          body: { samples: samples.map((s) => apiObject(s)) },
        }),
      ),
    meta: { errorToast: false },
  })
}

/**
 * Parse-check (and, with an event type, evaluate) a CEL expression. Keyed by
 * its inputs so identical expressions share one request.
 */
export const celCheckQuery = (body: CelCheckRequest) =>
  queryOptions({
    queryKey: ['tools', 'cel-check', body] as const,
    queryFn: ({ signal }) =>
      unwrap(api.POST('/admin/v1/tools/cel/check', { body, signal })) as Promise<CelCheckResponse>,
    staleTime: 60_000,
    retry: false,
  })

export function useTestSend() {
  return useMutation({
    mutationFn: ({ templateId, body }: { templateId: string; body: TestSendRequest }) =>
      unwrap(
        api.POST('/admin/v1/templates/{id}/test-send', {
          params: { path: { id: templateId } },
          body,
        }),
      ),
    meta: { invalidates: [messageKeys.all], errorToast: false },
  })
}

// ---------------------------------------------------------------------------
// Pickers: templates and senders available to an application
// ---------------------------------------------------------------------------

export const templateOptionsQuery = (app: string) =>
  queryOptions({
    queryKey: queryKeys.templates.list({ app, include_shared: true, all: true }),
    queryFn: () =>
      fetchAll((cursor) =>
        unwrap(
          api.GET('/admin/v1/templates', {
            params: { query: { app, include_shared: true, limit: MAX_PAGE_SIZE, cursor } },
          }),
        ),
      ),
    staleTime: 60_000,
  })

export const senderOptionsQuery = (app: string) =>
  queryOptions({
    queryKey: senderKeys.options(app),
    queryFn: () =>
      fetchAll((cursor) =>
        unwrap(
          api.GET('/admin/v1/senders', {
            params: { query: { app, include_shared: true, limit: MAX_PAGE_SIZE, cursor } },
          }),
        ),
      ),
    staleTime: 60_000,
  })

// ---------------------------------------------------------------------------
// Messages and requests
// ---------------------------------------------------------------------------

export const MESSAGE_PAGE_SIZE = 50

export function useMessagesPage(
  filters: MessageSearch & { app?: string; request_id?: string },
  options: { refetchInterval?: number | false } = {},
) {
  return useCursorPages({
    queryKey: messageKeys.list({ ...filters }),
    refetchInterval: options.refetchInterval,
    fetchPage: (cursor) =>
      unwrap(
        api.GET('/admin/v1/messages', {
          params: { query: { ...toMessageQuery(filters), limit: MESSAGE_PAGE_SIZE, cursor } },
        }),
      ),
  })
}

export const messageQuery = (id: string) =>
  queryOptions({
    queryKey: messageKeys.detail(id),
    queryFn: () => unwrap(api.GET('/admin/v1/messages/{id}', { params: { path: { id } } })),
  })

export function useResendMessage(id: string) {
  return useMutation({
    mutationFn: (body: ResendRequest) =>
      unwrap(api.POST('/admin/v1/messages/{id}/resend', { params: { path: { id } }, body })),
    meta: { invalidates: [messageKeys.all, requestKeys.all, queryKeys.stats.all] },
  })
}

export function useCancelMessage(id: string) {
  return useMutation({
    mutationFn: () =>
      unwrap(api.POST('/admin/v1/messages/{id}/cancel', { params: { path: { id } } })),
    meta: {
      // The timeline key root is ['timeline'] (features/timelines/api.ts).
      invalidates: [messageKeys.all, requestKeys.all, queryKeys.stats.all, ['timeline']],
      successMessage: 'Message cancelled',
    },
  })
}

export const requestQuery = (id: string) =>
  queryOptions({
    queryKey: requestKeys.detail(id),
    queryFn: () => unwrap(api.GET('/admin/v1/requests/{id}', { params: { path: { id } } })),
  })
